/**
 * Cross-Tab Synchronization Utilities for Hyperliquid API Wallet
 *
 * Three primitives:
 *  1. nextNonce()               — Monotonic nonce (fixes sub-ms collision + clock-backward)
 *  2. createWalletSyncChannel() — BroadcastChannel wrapper (fixes cache divergence)
 *  3. withMigrationLock()       — navigator.locks wrapper (fixes migration race)
 */

// ============================================================================
// 1. Monotonic Nonce
// ============================================================================

let lastNonce = 0;

/**
 * Returns a strictly increasing nonce based on Date.now().
 * If Date.now() hasn't advanced since the last call (sub-ms burst or
 * clock-backward), returns lastNonce + 1 instead.
 */
export function nextNonce(): number {
  return (lastNonce = Math.max(Date.now(), lastNonce + 1));
}

// ============================================================================
// 2. BroadcastChannel Wrapper
// ============================================================================

const CHANNEL_NAME = "perpsstudio-hl-wallet-sync";

export type WalletSyncMessage =
  | { type: "wallet-written"; userAddress: string; timestamp: number }
  | { type: "wallet-cleared"; userAddress: string; timestamp: number }
  | {
      type: "wallet-rotated";
      userAddress: string;
      timestamp: number;
      newVersion: number;
    };

export interface WalletSyncChannel {
  broadcast: (msg: WalletSyncMessage) => void;
  subscribe: (handler: (msg: WalletSyncMessage) => void) => void;
  close: () => void;
}

/**
 * Creates a cross-tab communication channel for wallet state changes.
 * When one tab writes to IndexedDB, it broadcasts so other tabs can
 * invalidate their in-memory caches.
 *
 * Returns a no-op stub if BroadcastChannel is unavailable (SSR, old browser).
 */
export function createWalletSyncChannel(): WalletSyncChannel {
  if (typeof BroadcastChannel === "undefined") {
    return { broadcast: () => {}, subscribe: () => {}, close: () => {} };
  }

  const channel = new BroadcastChannel(CHANNEL_NAME);

  return {
    broadcast(msg: WalletSyncMessage) {
      try {
        channel.postMessage(msg);
      } catch {
        // Channel may already be closed
      }
    },
    subscribe(handler: (msg: WalletSyncMessage) => void) {
      channel.onmessage = (event: MessageEvent) => {
        handler(event.data as WalletSyncMessage);
      };
    },
    close() {
      try {
        channel.close();
      } catch {
        // Already closed
      }
    },
  };
}

// ============================================================================
// 3. Migration Lock
// ============================================================================

const MIGRATION_LOCK_PREFIX = "perpsstudio-hl-migration";

/**
 * Serializes an async operation per user address using navigator.locks.
 * Prevents two tabs from migrating the same user's wallet concurrently.
 *
 * Falls back to direct invocation if navigator.locks is unavailable.
 */
export async function withMigrationLock<T>(
  userAddress: string,
  fn: () => Promise<T>,
): Promise<T> {
  if (typeof navigator === "undefined" || !navigator.locks) {
    return fn();
  }

  return navigator.locks.request(
    `${MIGRATION_LOCK_PREFIX}:${userAddress.toLowerCase()}`,
    () => fn(),
  );
}
